import React from 'react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import { AssignmentTurnedInOutlined, ChevronRightRounded, EastRounded, EventOutlined } from '@mui/icons-material';
import { TABLE_FONT_FAMILY } from '../../theme';
import { OPPORTUNITY_STATUS_LABELS } from '../../utils/commercialPlanning';
import { CommercialDashboard, formatDueLabel } from '../../utils/commercialDashboard';
import { ConsolePanel, EmptyNote } from './ConsolePanel';
import { inkOn, stageFill, useConsolePalette } from './consolePalette';

type TodoPanelProps = {
  dashboard: CommercialDashboard;
  onOpenLeads: () => void;
  onOpenLead: (opportunityId: number) => void;
};

const TodoPanel: React.FC<TodoPanelProps> = ({ dashboard, onOpenLeads, onOpenLead }) => {
  const palette = useConsolePalette();
  const { rows, total, overdue } = dashboard.todo;
  const subtitle = overdue > 0
    ? `Next actions on open leads · ${overdue} overdue`
    : 'Next actions on open leads';

  return (
    <ConsolePanel
      title="To do"
      subtitle={subtitle}
      icon={<AssignmentTurnedInOutlined />}
      action={total > 0 && (
        <Button
          size="small"
          onClick={() => onOpenLeads()}
          endIcon={<EastRounded fontSize="small" />}
          sx={{ color: palette.inkSoft, textTransform: 'none', fontWeight: 600 }}
        >
          View all {total}
        </Button>
      )}
      bodySx={{ p: 0, display: 'flex', flexDirection: 'column' }}
    >
      {rows.length === 0 ? (
        <Box sx={{ p: 2, flex: 1 }}>
          <EmptyNote>No next action is scheduled on an open lead. Set a next-action date on a lead to see it here.</EmptyNote>
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {rows.map((row) => {
            const fill = stageFill(row.status);
            const isOverdue = row.days_until < 0;
            const dueColor = isOverdue ? palette.fall : row.days_until <= 1 ? palette.figure : palette.inkSoft;
            const meta = [row.client_name, row.project_type].filter(Boolean).join(' · ') || 'No client set';

            return (
              <Box component="li" key={row.opportunity_id} sx={{ borderBottom: `1px solid ${palette.rule}`, '&:last-of-type': { borderBottom: 'none' } }}>
                <ButtonBase
                  onClick={() => onOpenLead(row.opportunity_id)}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '96px minmax(0, 1fr) auto 20px',
                    alignItems: 'center',
                    columnGap: 1.5,
                    px: 2,
                    py: 1.1,
                    textAlign: 'left',
                    transition: 'background-color 150ms ease-out',
                    '&:hover': { bgcolor: palette.hover },
                    '&.Mui-focusVisible': { bgcolor: palette.hover },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      justifySelf: 'start',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.75,
                      bgcolor: fill,
                      color: inkOn(fill),
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {OPPORTUNITY_STATUS_LABELS[row.status] ?? row.status}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600, color: palette.ink }}>
                      {row.title || 'Untitled lead'}
                    </Typography>
                    <Typography noWrap variant="caption" component="div" sx={{ color: palette.inkFaint }}>
                      {meta}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: dueColor }}>
                    <EventOutlined sx={{ fontSize: 15 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}
                    >
                      {formatDueLabel(row.days_until, row.next_action_date)}
                    </Typography>
                  </Box>
                  <ChevronRightRounded fontSize="small" sx={{ color: palette.inkFaint }} />
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      )}
    </ConsolePanel>
  );
};

export default TodoPanel;
